/**
 * DataTable Component
 * Sortable, filterable data table for reports
 */
import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Platform,
} from 'react-native';
import { DesktopTheme, getScoreColor } from '../theme/desktopTheme';

const TableHeader = ({ columns, sortConfig, onSort }) => {
  const [hoveredColumn, setHoveredColumn] = useState(null);

  return (
    <View style={styles.headerRow}>
      {columns.map((column) => (
        <TouchableOpacity
          key={column.key}
          style={[
            styles.headerCell,
            { flex: column.flex || 1, minWidth: column.minWidth },
            column.align === 'center' && { alignItems: 'center' },
            column.align === 'right' && { alignItems: 'flex-end' },
          ]}
          onPress={() => column.sortable && onSort(column.key)}
          onMouseEnter={
            Platform.OS === 'web' && column.sortable
              ? () => setHoveredColumn(column.key)
              : undefined
          }
          onMouseLeave={
            Platform.OS === 'web' ? () => setHoveredColumn(null) : undefined
          }
          disabled={!column.sortable}
        >
          <Text
            style={[
              styles.headerText,
              hoveredColumn === column.key && styles.headerTextHovered,
            ]}
          >
            {column.label}
            {sortConfig?.key === column.key && (
              <Text style={styles.sortIndicator}>
                {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
              </Text>
            )}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
};

const TableRow = ({ item, columns, onPress, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <TouchableOpacity
      style={[
        styles.dataRow,
        index % 2 === 0 && styles.dataRowEven,
        isHovered && styles.dataRowHovered,
      ]}
      onPress={() => onPress && onPress(item)}
      onMouseEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
      onMouseLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      activeOpacity={0.7}
    >
      {columns.map((column) => {
        const value = item[column.key];
        const displayValue = column.render ? column.render(value, item) : value;

        return (
          <View
            key={column.key}
            style={[
              styles.dataCell,
              { flex: column.flex || 1, minWidth: column.minWidth },
              column.align === 'center' && { alignItems: 'center' },
              column.align === 'right' && { alignItems: 'flex-end' },
            ]}
          >
            {typeof displayValue === 'string' || typeof displayValue === 'number' ? (
              <Text style={styles.cellText} numberOfLines={column.multiline ? 3 : 1}>
                {displayValue}
              </Text>
            ) : (
              displayValue
            )}
          </View>
        );
      })}
    </TouchableOpacity>
  );
};

const DataTable = ({
  data = [],
  columns = [],
  searchable = true,
  searchPlaceholder = 'Search...',
  searchKeys = [],
  onRowPress,
  emptyMessage = 'No data available',
  headerRight,
  style,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState(null);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return data;

    const query = searchQuery.toLowerCase();
    return data.filter((item) => {
      const keysToSearch = searchKeys.length > 0 ? searchKeys : Object.keys(item);
      return keysToSearch.some((key) => {
        const value = item[key];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(query);
      });
    });
  }, [data, searchQuery, searchKeys]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      let comparison = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        if (current.direction === 'asc') {
          return { key, direction: 'desc' };
        }
        return null; // Remove sort
      }
      return { key, direction: 'asc' };
    });
  };

  return (
    <View style={[styles.container, style]}>
      {/* Search Bar */}
      {searchable && (
        <View style={styles.toolbar}>
          <View style={styles.searchContainer}>
            <Text style={styles.searchIcon}>🔍</Text>
            <TextInput
              style={styles.searchInput}
              placeholder={searchPlaceholder}
              placeholderTextColor={colors.textMuted}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Text style={styles.clearIcon}>✕</Text>
              </TouchableOpacity>
            )}
          </View>
          {headerRight && <View style={styles.headerRightContainer}>{headerRight}</View>}
        </View>
      )}

      {/* Results count */}
      <Text style={styles.resultsCount}>
        {sortedData.length} {sortedData.length === 1 ? 'result' : 'results'}
        {searchQuery && ` for "${searchQuery}"`}
      </Text>

      {/* Table */}
      <View style={styles.tableContainer}>
        <TableHeader
          columns={columns}
          sortConfig={sortConfig}
          onSort={handleSort}
        />

        <ScrollView
          style={styles.tableBody}
          showsVerticalScrollIndicator={true}
        >
          {sortedData.length > 0 ? (
            sortedData.map((item, index) => (
              <TableRow
                key={item.id || index}
                item={item}
                columns={columns}
                onPress={onRowPress}
                index={index}
              />
            ))
          ) : (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyIcon}>📋</Text>
              <Text style={styles.emptyText}>{emptyMessage}</Text>
            </View>
          )}
        </ScrollView>
      </View>
    </View>
  );
};

// Score badge component for use in columns
export const ScoreBadge = ({ score }) => {
  const color = getScoreColor(score);
  return (
    <View style={[scoreBadgeStyles.badge, { backgroundColor: color + '20' }]}>
      <Text style={[scoreBadgeStyles.text, { color }]}>{Math.round(score)}%</Text>
    </View>
  );
};

// Trend indicator component for use in columns
export const TrendIndicator = ({ current, previous }) => {
  if (previous === null || previous === undefined) return null;

  const trend = current - previous;
  const color =
    trend > 0 ? colors.success : trend < 0 ? colors.error : colors.textMuted;
  const icon = trend > 0 ? '↑' : trend < 0 ? '↓' : '→';

  return (
    <View style={trendStyles.container}>
      <Text style={[trendStyles.icon, { color }]}>{icon}</Text>
      <Text style={[trendStyles.value, { color }]}>{Math.abs(Math.round(trend))}</Text>
    </View>
  );
};

const { colors, typography, spacing, borderRadius, shadows } = DesktopTheme;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.cardBg,
    borderRadius: borderRadius.lg,
    ...shadows.card,
    overflow: 'hidden',
  },

  // Toolbar
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  searchContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.contentBg,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    maxWidth: 400,
  },
  searchIcon: {
    fontSize: 16,
    marginRight: spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textPrimary,
    outline: 'none',
  },
  clearIcon: {
    fontSize: 14,
    color: colors.textMuted,
    marginLeft: spacing.sm,
  },
  headerRightContainer: {
    marginLeft: spacing.md,
  },

  // Results
  resultsCount: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.contentBg,
  },

  // Table
  tableContainer: {
    flex: 1,
  },
  headerRow: {
    flexDirection: 'row',
    backgroundColor: colors.contentBg,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerCell: {
    paddingHorizontal: spacing.sm,
  },
  headerText: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.small,
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  headerTextHovered: {
    color: colors.primary,
  },
  sortIndicator: {
    color: colors.primary,
  },

  // Body
  tableBody: {
    flex: 1,
  },
  dataRow: {
    flexDirection: 'row',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  dataRowEven: {
    backgroundColor: colors.contentBg + '50',
  },
  dataRowHovered: {
    backgroundColor: colors.primaryLight,
  },
  dataCell: {
    paddingHorizontal: spacing.sm,
    justifyContent: 'center',
  },
  cellText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textPrimary,
  },

  // Empty state
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xxl * 2,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: spacing.md,
  },
  emptyText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textMuted,
  },
});

const scoreBadgeStyles = StyleSheet.create({
  badge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.sm,
    alignSelf: 'flex-start',
  },
  text: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.small,
  },
});

const trendStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    fontSize: typography.fontSize.body,
    marginRight: 2,
  },
  value: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.small,
  },
});

export default DataTable;
